import type { ComponentType, ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface FormSectionProps {
  /** Section heading (top-left). */
  title: ReactNode;
  /** Optional lucide-style icon next to the title. */
  icon?: ComponentType<{ className?: string }>;
  /** Wrapper div around the icon (use for coloured tile backgrounds). */
  iconWrapperClassName?: string;
  /** Override class for the icon glyph itself. */
  iconClassName?: string;
  /** Right-aligned header slot — typically Save / cancel buttons. */
  actions?: ReactNode;
  /** Number of equal columns the grid renders at md+. */
  columns?: 1 | 2 | 3 | 4;
  /** When true, dims the section and blocks pointer events. */
  disabled?: boolean;
  /** Form fields. Rendered inside a `grid gap-3` of the configured column count. */
  children: ReactNode;
}

const COLUMN_CLASS: Record<NonNullable<FormSectionProps['columns']>, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-3',
  4: 'grid-cols-1 md:grid-cols-4',
};

/**
 * Titled card grouping a set of form fields. Lifted near-verbatim from
 * My.Tms.Old/components/FormSection.tsx; the `actions` slot is the
 * standard place for per-section Save buttons.
 */
export function FormSection({
  title,
  icon: Icon,
  iconWrapperClassName,
  iconClassName,
  actions,
  columns = 2,
  disabled = false,
  children,
}: FormSectionProps) {
  return (
    <section
      className={cn(
        'rounded-sm border border-slate-300 bg-white p-3 shadow-sm',
        disabled && 'pointer-events-none opacity-60',
      )}
      aria-disabled={disabled || undefined}
    >
      <h2 className="mb-4 flex items-center justify-between text-base font-semibold text-slate-900">
        <span className="flex items-center gap-1.5">
          {Icon && (
            <span className={iconWrapperClassName}>
              <Icon className={cn('h-5 w-5', iconClassName)} />
            </span>
          )}
          {title}
        </span>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </h2>
      <div className={cn('grid gap-3', COLUMN_CLASS[columns])}>{children}</div>
    </section>
  );
}
